import { useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Download, FileText, UserCheck, UserX, Users, Search, Award } from "lucide-react";

const STATUS_COLORS = {
  actif: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sortant: "bg-amber-50 text-amber-700 border-amber-200",
  exclu: "bg-rose-50 text-rose-700 border-rose-200",
  suspendu: "bg-slate-100 text-slate-700 border-slate-300",
};

const TERRITOIRES = ["", "Guadeloupe", "Martinique", "Guyane", "La Réunion", "Mayotte", "Saint-Martin", "Saint-Pierre-et-Miquelon", "Polynésie", "Nouvelle-Calédonie"];

export default function RegistreMembres() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAnnee, setFilterAnnee] = useState("");
  const [filterTerritoire, setFilterTerritoire] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [filterKyc, setFilterKyc] = useState("");
  const [search, setSearch] = useState("");
  const [statusDialog, setStatusDialog] = useState(null); // {member, newStatut}
  const [motif, setMotif] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAnnee) params.annee = Number(filterAnnee);
      if (filterTerritoire) params.territoire = filterTerritoire;
      if (filterStatut) params.statut = filterStatut;
      if (filterKyc) params.kyc = filterKyc;
      const { data } = await api.get("/membres-registre", { params });
      setMembers(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [filterAnnee, filterTerritoire, filterStatut, filterKyc]);

  useEffect(() => { load(); }, [load]);

  const filtered = members.filter(m => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (m.raison_sociale || "").toLowerCase().includes(s)
      || (m.siret || "").includes(s)
      || (m.numero_membre || "").toLowerCase().includes(s);
  });

  const downloadCSV = async () => {
    try {
      const resp = await api.get("/membres-registre/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "registre-membres-oscopge.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export CSV impossible");
    }
  };

  const downloadZip = async () => {
    try {
      const resp = await api.get("/membres-registre/attestations/zip", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `attestations-oscopge-${new Date().toISOString().slice(0,10).replaceAll("-","")}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("ZIP téléchargé");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Aucun membre actif à exporter");
    }
  };

  const updateStatut = async () => {
    if (!statusDialog) return;
    try {
      await api.patch(`/membres-registre/${statusDialog.member.id}`, {
        statut: statusDialog.newStatut,
        motif: motif || undefined,
      });
      toast.success(`Membre ${statusDialog.newStatut}`);
      setStatusDialog(null); setMotif("");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur mise à jour");
    }
  };

  const stats = {
    total: members.length,
    actifs: members.filter(m => m.statut === "actif").length,
    sortants: members.filter(m => m.statut === "sortant").length,
    cotisation_ttc: members.filter(m => m.statut === "actif").reduce((sum, m) => sum + (m.cotisation_ttc || 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="registre-membres-page">
      <PageHeader
        title="Registre des membres"
        subtitle="Enregistrement automatique des entreprises membres après paiement de leur bulletin d'adhésion."
        icon={Users}
        actions={
          <div className="flex gap-2">
            <Button onClick={downloadCSV} variant="outline" data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" />Export CSV
            </Button>
            <Button onClick={downloadZip} className="bg-[#C5A059] hover:bg-[#A88846]" data-testid="export-attestations-zip">
              <Award className="h-4 w-4 mr-2" />ZIP attestations actives
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Membres enregistrés" value={stats.total} />
        <KpiCard label="Membres actifs" value={stats.actifs} accent="emerald" />
        <KpiCard label="Membres sortants" value={stats.sortants} accent="amber" />
        <KpiCard label="Cotisation TTC active" value={`${stats.cotisation_ttc.toFixed(2)} €`} accent="gold" />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 items-center bg-white rounded-xl p-4 border border-slate-200">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Recherche raison sociale, SIRET, n° membre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <Select value={filterAnnee} onValueChange={setFilterAnnee}>
          <SelectTrigger className="w-32" data-testid="filter-annee"><SelectValue placeholder="Année" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes années</SelectItem>
            {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTerritoire} onValueChange={setFilterTerritoire}>
          <SelectTrigger className="w-48" data-testid="filter-territoire"><SelectValue placeholder="Territoire" /></SelectTrigger>
          <SelectContent>
            {TERRITOIRES.map(t => <SelectItem key={t || "all"} value={t || "all"}>{t || "Tous territoires"}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatut} onValueChange={setFilterStatut}>
          <SelectTrigger className="w-40" data-testid="filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="actif">Actif</SelectItem>
            <SelectItem value="sortant">Sortant</SelectItem>
            <SelectItem value="exclu">Exclu</SelectItem>
            <SelectItem value="suspendu">Suspendu</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterKyc} onValueChange={setFilterKyc}>
          <SelectTrigger className="w-48" data-testid="filter-kyc"><SelectValue placeholder="Dossier KYC" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous dossiers KYC</SelectItem>
            <SelectItem value="verified">Validées (KYC)</SelectItem>
            <SelectItem value="rejected">Rejetées (KYC)</SelectItem>
            <SelectItem value="pending">En attente (KYC)</SelectItem>
            <SelectItem value="archived">Archivées (KYC)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>N° Membre</TableHead>
                <TableHead>Raison sociale</TableHead>
                <TableHead>SIRET</TableHead>
                <TableHead>Territoire</TableHead>
                <TableHead>Date entrée</TableHead>
                <TableHead>Période d'accès</TableHead>
                <TableHead className="text-right">Cotisation TTC</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Chargement…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Aucun membre enregistré.</TableCell></TableRow>
              ) : filtered.map(m => (
                <TableRow key={m.id} data-testid={`member-row-${m.id}`}>
                  <TableCell className="font-mono text-xs">{m.numero_membre}</TableCell>
                  <TableCell className="font-medium">{m.raison_sociale}</TableCell>
                  <TableCell className="font-mono text-xs">{m.siret}</TableCell>
                  <TableCell>{m.territoire}</TableCell>
                  <TableCell>{m.date_entree}</TableCell>
                  <TableCell className="text-xs">
                    {m.periode_debut}<br/>→ {m.periode_fin}<br/>
                    <span className="text-slate-500">({m.mois_actifs}/12 mois)</span>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-[#A88846]">{(m.cotisation_ttc || 0).toFixed(2)} €</TableCell>
                  <TableCell>
                    <Badge className={STATUS_COLORS[m.statut] || "bg-slate-100"} variant="outline">{m.statut}</Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {m.statut === "actif" && (
                      <Button size="sm" variant="ghost" onClick={() => setStatusDialog({ member: m, newStatut: "sortant" })} data-testid="btn-sortant">
                        <UserX className="h-4 w-4" />
                      </Button>
                    )}
                    {m.statut !== "actif" && (
                      <Button size="sm" variant="ghost" onClick={() => setStatusDialog({ member: m, newStatut: "actif" })} data-testid="btn-reactiver">
                        <UserCheck className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" asChild>
                      <a href={`${API}/entreprises/${m.entreprise_id}/adhesion/${m.appel_reference}/pdf`} target="_blank" rel="noopener noreferrer" title="Bulletin PDF">
                        <FileText className="h-4 w-4" />
                      </a>
                    </Button>
                    {m.statut === "actif" && (
                      <Button size="sm" variant="ghost" asChild title="Attestation de membre" data-testid={`attestation-${m.id}`}>
                        <a href={`${API}/membres-registre/${m.id}/attestation.pdf`} target="_blank" rel="noopener noreferrer">
                          <Award className="h-4 w-4 text-[#C5A059]" />
                        </a>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog changement statut */}
      <Dialog open={!!statusDialog} onOpenChange={(v) => { if (!v) { setStatusDialog(null); setMotif(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer le statut du membre</DialogTitle>
          </DialogHeader>
          {statusDialog && (
            <div className="space-y-3">
              <p className="text-sm">
                Membre <strong>{statusDialog.member.raison_sociale}</strong> → <Badge className={STATUS_COLORS[statusDialog.newStatut]}>{statusDialog.newStatut}</Badge>
              </p>
              <Textarea placeholder="Motif (optionnel)" value={motif} onChange={(e) => setMotif(e.target.value)} data-testid="motif-textarea" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStatusDialog(null); setMotif(""); }}>Annuler</Button>
            <Button onClick={updateStatut} className="bg-[#C5A059] hover:bg-[#A88846]" data-testid="confirm-statut-btn">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  const accents = {
    emerald: "border-emerald-200 bg-emerald-50/60",
    amber: "border-amber-200 bg-amber-50/60",
    gold: "border-[#C5A059]/40 bg-[#FDF9F1]",
  };
  return (
    <div className={`rounded-xl border p-4 ${accents[accent] || "border-slate-200 bg-white"}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-bold mt-1 text-slate-900">{value}</div>
    </div>
  );
}
